import type { CityConfig } from "@/pages/city-template/types";

export const lincolnCityConfig: CityConfig = {
  city: "Lincoln City",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "lincoln-city",
  route: "/cities/lincoln-city",
  title: "Emergency Passport Lincoln City OR | Expedited Passport Lincoln County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Lincoln City, OR — as fast as 24 hours. Fast Passport Center serves Lincoln City, Newport, Depoe Bay, Siletz, and Lincoln County. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Lincoln City, Oregon",
  heroSubtitle: "Mexico cruise leaving soon from LA? Canada ski trip and passport expired? International trip booked and you live on the central Oregon coast? Fast Passport Center helps Lincoln City and Lincoln County residents get expedited passports in as fast as 24 hours — entirely remotely, without a 3-hour drive to Portland or a trip to Seattle.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lincoln%20City%20Oregon%20central%20coast%20with%20Devils%20Lake%20visible%20inland%20behind%20the%20beach%2C%20dramatic%20Pacific%20Ocean%20waves%20breaking%20on%20wide%20sandy%20beach%20with%20coastal%20dunes%2C%20Oregon%20coast%20range%20visible%20inland%2C%20overcast%20Pacific%20Northwest%20sky%20with%20moody%20dramatic%20light%2C%20Devils%20Punchbowl-style%20rock%20formations%20in%20distance%2C%20wide%20angle%20coastal%20panorama%2C%20photorealistic%20high%20quality%2C%20no%20text%20no%20people&width=1800&height=700&seq=lincoln-city-or-hero-01&orientation=landscape",
  heroImageAlt: "Lincoln City Oregon central coast with Pacific Ocean and coastal dunes",
  geoPosition: "44.9579;-124.0071",
  geoPlacename: "Lincoln City, Oregon",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 320 miles north — approximately 5.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Lincoln City", zips: "97367" },
    { name: "Depoe Bay", zips: "97341" },
    { name: "Newport", zips: "97365" },
    { name: "Siletz", zips: "97380" },
    { name: "Toledo", zips: "97391" },
    { name: "Waldport", zips: "97394" },
    { name: "Otis", zips: "97368" },
    { name: "Neskowin", zips: "97149" },
    { name: "Tillamook", zips: "97141" },
    { name: "McMinnville", zips: "97128" },
    { name: "Corvallis", zips: "97330, 97331, 97333" },
    { name: "Salem", zips: "97301–97317" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Lincoln City", desc: "Complete your emergency or expedited passport order from anywhere on the central Oregon coast — no three-hour drive to Portland needed." },
    { icon: "ri-truck-line", title: "Ship Overnight via Portland or Salem", desc: "Portland FedEx (about 90 miles east on US-18) or Salem overnight shipping options make Fast Passport Center accessible from Lincoln City." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — essential for central coast residents who can't easily visit a passport acceptance office." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment system with reserved Seattle Passport Agency slots — no 5.5-hour drive from Lincoln City required." },
  ],
  testimonials: [
    {
      name: "Dennis W.",
      location: "Lincoln City, OR",
      rating: 5,
      text: "As a Lincoln City resident, getting to Portland for anything government-related is a half-day commitment. Fast Passport Center solved the problem entirely — I shipped from a Salem FedEx on a shopping run and had my passport back in 8 days. This is the way.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Susan K.",
      location: "Depoe Bay, OR",
      rating: 5,
      text: "We had a Mexico cruise leaving from Los Angeles in two weeks and my passport expired. Fast Passport Center got it renewed in 7 business days. I shipped from a Portland FedEx on a Costco run. They were professional and clear the whole way through. Made the cruise!",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Mark P.",
      location: "Newport, OR",
      rating: 5,
      text: "Newport resident — needed an emergency passport for a BC family matter. Fast Passport Center handled it in 5 business days. Their document checklist was perfect, and the overnight shipping from Portland was easy. Can't recommend them highly enough for Oregon coast residents.",
      service: "Emergency Passport Processing",
    },
  ],
  facilities: [
    {
      id: "lincoln-city-post",
      name: "Lincoln City Post Office",
      type: "USPS Passport Acceptance",
      address: "1501 SE Devil's Lake Rd",
      city: "Lincoln City",
      state: "OR",
      zip: "97367",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Lincoln City — primary passport acceptance for Lincoln City and Devils Lake area residents",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Lincoln City residents. Appointment required. Availability can be limited — book well in advance, especially during summer beach season.",
      icon: "ri-mail-line",
    },
    {
      id: "depoe-bay-post",
      name: "Depoe Bay Post Office",
      type: "USPS Passport Acceptance",
      address: "39 SE Hwy 101",
      city: "Depoe Bay",
      state: "OR",
      zip: "97341",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Depoe Bay — about 12 miles south of Lincoln City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Very small facility — extremely limited appointment availability. Lincoln City or Newport preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "newport-post-main",
      name: "Newport Post Office",
      type: "USPS Passport Acceptance",
      address: "310 SW 2nd St",
      city: "Newport",
      state: "OR",
      zip: "97365",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Newport — about 25 miles south of Lincoln City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Newport post office serves Lincoln County residents south of Lincoln City. Better capacity than Depoe Bay.",
      icon: "ri-mail-line",
    },
    {
      id: "lincoln-county-clerk",
      name: "Lincoln County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "225 W Olive St",
      city: "Newport",
      state: "OR",
      zip: "97365",
      phone: "(541) 265-4131",
      hours: "Mon–Fri 8am–5pm",
      distance: "Newport — about 25 miles south of Lincoln City, Lincoln County Courthouse",
      website: "https://www.co.lincoln.or.us/clerk",
      websiteLabel: "Lincoln County Clerk",
      notes: "Lincoln County Clerk in Newport often has better appointment availability than post offices. Recommended for urgent Lincoln City-area passport applications.",
      icon: "ri-government-line",
    },
    {
      id: "tillamook-post",
      name: "Tillamook Post Office",
      type: "USPS Passport Acceptance",
      address: "2200 N Main Ave",
      city: "Tillamook",
      state: "OR",
      zip: "97141",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Tillamook — about 35 miles north of Lincoln City via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tillamook post office is an alternative for north Lincoln County residents who need a facility with better appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "mcminnville-post",
      name: "McMinnville Post Office",
      type: "USPS Passport Acceptance",
      address: "116 SE 1st St",
      city: "McMinnville",
      state: "OR",
      zip: "97128",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "McMinnville — about 35 miles east via OR-18 from Lincoln City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "McMinnville is accessible from Lincoln City via OR-18 — a convenient inland alternative with better appointment availability for urgent passport needs.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Lincoln City, Oregon?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Lincoln City and Lincoln County residents as fast as 24 hours. We serve the central Oregon coast entirely remotely — ship overnight from Portland FedEx (90 miles east on US-18) or Salem, and we handle the Seattle agency submission.",
    },
    {
      q: "How fast can I get an expedited passport from Lincoln City, OR?",
      a: "Expedited passport processing from Lincoln City takes 2–9 business days through Fast Passport Center. Most adult renewals don't require in-person acceptance — ship overnight from Portland or Salem on your next inland trip.",
    },
    {
      q: "Where is the passport acceptance office in Lincoln City?",
      a: "Lincoln City Post Office (1501 SE Devil's Lake Rd) is the primary acceptance facility. Depoe Bay has very limited availability. Newport (25 miles south) and the Lincoln County Clerk in Newport often have better appointment availability. McMinnville (35 miles east on OR-18) is a convenient inland alternative.",
    },
    {
      q: "How far is Lincoln City from the Seattle Passport Agency?",
      a: "The Seattle Passport Agency is approximately 320 miles north of Lincoln City — about 5.5 hours by car. For life-or-death emergencies, a Seattle trip may be necessary. For most urgent travel (2–9 business days), Fast Passport Center serves Lincoln City entirely remotely.",
    },
    {
      q: "Can I renew my passport in Lincoln City without going to an office?",
      a: "Yes. Most adult Lincoln City residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from Portland FedEx on your next Costco or shopping run east on US-18.",
    },
    {
      q: "What airport do Lincoln City residents use for international travel?",
      a: "Portland International Airport (PDX) is the primary international hub for Lincoln City residents — about 90 miles east via US-18 through the Coast Range (about 1.5–2 hours). PDX serves direct international routes to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City.",
    },
    {
      q: "Do Siletz Tribal Nation members get special passport services?",
      a: "Fast Passport Center serves all Lincoln County residents equally, including Confederated Tribes of Siletz Indians members. Tribal members traveling internationally for cultural, health, or government purposes are eligible for the same expedited passport services as other Lincoln City-area residents.",
    },
    {
      q: "Can I get a child passport from Lincoln City?",
      a: "Yes. Child passports require an in-person acceptance visit at the Lincoln City Post Office or another accepted facility — both parents must typically be present or provide notarized consent. Fast Passport Center provides the child passport checklist and expedites from there.",
    },
    {
      q: "Is US-18 from Lincoln City to Portland reliable for overnight shipping runs?",
      a: "US-18 through the Coast Range is generally reliable, though winter mountain pass conditions can cause delays. Fall and spring are the best seasons for quick Portland shipping runs from Lincoln City. Fast Passport Center accounts for your location in the processing timeline estimate.",
    },
    {
      q: "How much does expedited passport service cost from Lincoln City?",
      a: "Fast Passport Center pricing includes U.S. government passport fees plus our courier service fee — typically 30–100% less than FedEx Office or retail passport services. We provide an exact price quote at checkout before any commitment.",
    },
  ],
  photoLocations: [
    { name: "Walmart — Lincoln City", address: "3521 SE Hwy 101, Lincoln City, OR 97367", hours: "Daily 6 AM – 11 PM" },
    { name: "Fred Meyer — Lincoln City", address: "2800 NW Highway 101, Lincoln City, OR 97367", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Lincoln City", address: "1115 SE Hwy 101, Lincoln City, OR 97367", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Newport", address: "140 SE Harney St, Newport, OR 97365", hours: "Daily 6 AM – 11 PM" },
    { name: "FedEx Office — Salem", address: "3015 Commercial St SE, Salem, OR 97302", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "FedEx Office — Portland Beaverton", address: "3935 SW 109th Ave, Beaverton, OR 97005", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 6 PM" },
    { name: "Walgreens — McMinnville", address: "555 NE 27th St, McMinnville, OR 97128", hours: "Daily 8 AM – 10 PM" },
    { name: "Costco Photo — Salem", address: "933 Lancaster Dr NE, Salem, OR 97301", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "The UPS Store — Corvallis", address: "1571 SW 53rd St, Corvallis, OR 97333", hours: "Mon–Fri 8 AM – 6:30 PM, Sat 9 AM – 4 PM" },
    { name: "Walmart Photo — Tillamook", address: "2404 N Main Ave, Tillamook, OR 97141", hours: "Daily 7 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Lincoln City Driver & Motor Vehicle Services",
      address: "1501 SE Devil's Lake Rd, Lincoln City, OR 97367",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Lincoln City DMV — driver licenses, REAL ID, and state ID cards for Lincoln City and Depoe Bay area residents.",
      featured: true,
    },
    {
      name: "Newport Driver & Motor Vehicle Services",
      address: "Hwy 101, Newport, OR 97365",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Newport DMV — serves Lincoln County south of Lincoln City including Newport and Waldport.",
      featured: false,
    },
    {
      name: "McMinnville Driver & Motor Vehicle Services",
      address: "2085 OR-18, McMinnville, OR 97128",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "McMinnville DMV — accessible via OR-18 from Lincoln City for Yamhill County and inland valley services.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international hub — about 90 miles east via US-18 and OR-99W (1.5–2 hours)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport is the primary international departure hub for Lincoln City and Lincoln County residents. US-18 through the Coast Range connects Lincoln City to the Portland metro area. PDX serves direct international routes to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "About 320 miles north (5.5 hours — not practical for most Lincoln City travelers)",
      address: "17801 International Blvd, Seattle, WA 98158",
      note: "SEA-TAC is an alternative international hub but is far from Lincoln City. Portland International (PDX) is strongly preferred for all Lincoln County international travel.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA-TAC Airport Website",
      featured: false,
    },
    {
      code: "EUG",
      name: "Eugene Airport",
      distance: "About 80 miles southeast via US-20 and OR-34 (1.5–2 hours)",
      address: "28801 Douglas Dr, Eugene, OR 97402",
      note: "Eugene Airport (EUG) is an alternative for Lincoln City residents heading south — connections to San Francisco, Los Angeles, Denver, and Seattle for onward international travel.",
      flightUrl: "https://www.eugeneairport.com/",
      flightLabel: "Eugene Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Lincoln City & Central Oregon Coast",
      description: "Mexico cruise leaving from LA in 10 days? Canada family emergency? International business trip and passport expired? Fast Passport Center handles emergency passport processing for Lincoln City and Lincoln County residents as fast as 24 hours — entirely remotely.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired during central coast years? Lincoln City residents renew fast — ship overnight from Portland FedEx on your next US-18 grocery run, and we handle the expedited submission. Passport back in 2–9 business days.",
      bullets: ["Fastest renewal for central Oregon coast residents", "Ship overnight from Portland or Salem", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Lincoln City or before international travel? We guide Lincoln County residents through DS-64 and expedited replacement — no Seattle road trip.",
      bullets: ["DS-64 loss/theft reporting guidance", "Expedited replacement timelines", "Pre-submission document review"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Lincoln County Families",
      description: "Family Mexico or Canada trip? Child passports require an in-person acceptance step at the Lincoln City Post Office or Newport facility. We provide the checklist and expedite from there.",
      bullets: ["Both parents typically required to consent", "Lincoln City and Newport acceptance guidance", "Expedited processing after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Lincoln City? In-person acceptance is required for first-timers. We guide you through the Lincoln City or Newport acceptance step and expedite from there.",
      bullets: ["Step-by-step acceptance guidance for central coast facilities", "Photo location guide for Lincoln City", "Expedited processing after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change after marriage, damaged passport replacement, and second passports for frequent travelers in Lincoln City, Depoe Bay, and Newport.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passport for frequent travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Lincoln City, Oregon residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Lincoln City and Lincoln County residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Lincoln City, Oregon — ship overnight from Portland or Salem." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Lincoln City and Depoe Bay travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Lincoln City and Lincoln County families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Lincoln City, Newport, and the central Oregon coast." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking Mexico Cruises Without Checking Passport Expiration", description: "Mexico cruises from Los Angeles, Seattle, or San Francisco are popular with Lincoln City-area residents. Many book months ahead but don't check passport expiration until packing — often 1–2 weeks before departure. Check passport validity the same day you book." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "Photos from Walmart or Fred Meyer frequently get rejected for lighting or expression issues. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — costing weeks. Our pre-submission review catches all errors before they cause delays." },
    { icon: "ri-pen-nib-line", title: "Using the Wrong Birth Certificate", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed before you mail anything." },
    { icon: "ri-map-pin-time-line", title: "Underestimating Coast-to-City Shipping Time on US-18", description: "The US-18 Coast Range drive from Lincoln City to Portland takes 1.5–2 hours depending on conditions. Plan your Portland shipping run with time to spare — and avoid Coast Range passes during winter storms." },
    { icon: "ri-calendar-close-line", title: "Missing the Six-Month Validity Requirement", description: "Mexico, Canada (by air), and most international destinations require passports valid for 6 months beyond your return date. A passport expiring in 3 months is functionally expired for most international trips from Lincoln City." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online from Lincoln City", description: "Choose your processing speed. Complete your secure order from Lincoln City or Newport in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with central Oregon coast shipping guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Overnight on Your Next Portland Run", description: "Drive US-18 east on your next Costco or shopping trip — drop off at Portland FedEx or Beaverton FedEx Office. Or use Salem FedEx via OR-18/US-22." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — no 5.5-hour drive from Lincoln City required." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Lincoln City Door", description: "Your completed passport ships directly to your Lincoln City or central coast address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 5.5 hours from Lincoln City",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Extremely impractical for central coast residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Portland or Salem",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; longer during peak summer",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive 5.5 hours to Seattle from Lincoln City", fpc: "Ship overnight from Portland (90 miles east)" },
    { feature: "Processing Speed", agency: "Strict emergency eligibility", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Central Coast Accessibility", agency: "Not designed for remote coast residents", fpc: "Remote service — ship from Portland or Salem" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 11-hour round trip to Seattle", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Newport", route: "/cities/newport" },
    { name: "Emergency passport help in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Salem", route: "/cities/salem" },
    { name: "Rush passport services for Oregon residents", route: "/states/oregon" },
    { name: "Expedited passports statewide in Oregon", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order, we send your personalized checklist and overnight shipping instructions. Drive US-18 east to Portland FedEx on your next city run — about 90 miles. Or use the Salem FedEx via US-18/US-22. No visit to our processing facility required.",
  fpcOfficeDistance: "Serving Lincoln City, Newport, and the central Oregon coast remotely",
  serviceAreaIntro: "Whether you are in Lincoln City, Depoe Bay, Newport, Siletz, Toledo, Waldport, or anywhere in Lincoln County — Fast Passport Center handles emergency and expedited passports without the drive to Seattle.",
  nearbyCitiesIntro: "We serve all Lincoln County zip codes and surrounding communities including Depoe Bay, Newport, Siletz, Toledo, Waldport, Otis, Neskowin, Tillamook, and McMinnville.",
  howItWorksIntro: "Getting an emergency or expedited passport from Lincoln City is simpler than a 5.5-hour drive to Seattle. Ship overnight from Portland on your next shopping run — most coast clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Lincoln City and Lincoln County passport applicants. We review your documents before submission.",
  testimonialsIntro: "Real reviews from Lincoln City, Newport, and central Oregon coast travelers who needed their passports fast.",
  faqIntro: "The most common questions Lincoln City and Lincoln County residents ask about emergency and expedited passport services.",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (Oregon Vital Records)", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/", desc: "Oregon Health Authority — Vital Records for birth and death certificates statewide" },
    { label: "Marriage Licenses (Lincoln County Clerk)", url: "https://www.co.lincoln.or.us/clerk", desc: "Lincoln County Clerk for Lincoln City and central Oregon coast" },
    { label: "Divorce Decrees (Lincoln County Circuit Court)", url: "https://www.courts.oregon.gov/courts/lincoln/", desc: "Lincoln County Circuit Court for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Oregon vital records online via VitalChek" },
  ],
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Lincoln City and Central Oregon Coast Residents",
    intro: "Lincoln City and the central Oregon coast sit at the intersection of Willamette Valley accessibility (90 miles to Portland on US-18) and true Pacific remoteness. This creates a unique balance: access to Portland's international hub airport, but day-to-day life far from major passport acceptance facilities. Travel patterns reflect the central coast's character — Mexico winter escapes, Canada outdoors, Alaska cruises, and Hawaii beach trips.",
    cards: [
      {
        icon: "ri-ship-2-line",
        title: "Mexico & Caribbean Cruises from LA, Seattle, or San Francisco",
        subtitle: "The most common international urgency for Lincoln City-area residents",
        description: "Mexico Pacific coast cruises from Los Angeles (Puerto Vallarta, Mazatlan, Cabo), Hawaii cruises from Ensenada, and Caribbean cruises from various ports are popular with central Oregon coast residents. These cruises are booked 6–12 months in advance — and passport checks happen right before departure, not at booking. Fast Passport Center expedites passports for Lincoln City-area cruise travelers heading to Mexico and the Caribbean in 2–9 business days.",
        tip: "Mexico cruise bookings happen in September–October for the following winter season. Check your passport the day you book. If it expires before June of the cruise year, renew immediately. Fast Passport Center handles expedited renewals in 2–9 business days from Lincoln City via Portland FedEx.",
      },
      {
        icon: "ri-map-pin-2-line",
        title: "British Columbia — Vancouver, Whistler, Gulf Islands",
        subtitle: "BC is the most accessible international destination for central coast residents",
        description: "British Columbia is Lincoln City's most accessible international destination — Vancouver is about 5 hours north via US-18, I-5 north, and Blaine crossing. Whistler is popular for ski trips. Gulf Islands and Tofino on the BC west coast attract central Oregon coast residents for Pacific Rim outdoors experiences. Victoria is reachable by ferry from Port Angeles WA. All BC air travel requires a passport book — land crossings accept passport cards.",
        tip: "For BC skiing at Whistler, book your trip in November and check passport expiration immediately. December–January departures need passport renewals submitted by October at the latest. Fast Passport Center expedites from Lincoln City via Portland FedEx in 2–9 business days.",
      },
      {
        icon: "ri-sun-line",
        title: "Hawaii — Pacific Island Getaways",
        subtitle: "Hawaii trips from Portland don't require a passport — but it's a travel document best practice",
        description: "Hawaii is technically a U.S. state and doesn't require a passport for Americans — but Lincoln City residents booking Hawaii trips often combine them with Japan or Pacific Rim travel, which does require a passport. Additionally, Hawaii travel promotes broader international travel habits. Central coast residents often discover their passport is expired during Hawaii planning when they consider adding a Japanese extension. Fast Passport Center handles expedited renewals for Lincoln City Hawaii-and-beyond travelers.",
        tip: "If you're adding Japan, Australia, or New Zealand to your Hawaii trip, you need a valid passport. Check expiration immediately when booking any Pacific trip with international extensions. Fast Passport Center expedites from Lincoln City in 2–9 business days.",
      },
      {
        icon: "ri-sun-cloudy-line",
        title: "Mexico & Costa Rica Winter Direct Travel",
        subtitle: "Central coast residents escape to Mexico and Costa Rica November through April",
        description: "Lincoln City winters are gray and stormy — making Mexico, Costa Rica, and Belize enormously appealing from November through April. Puerto Vallarta and Cabo are popular direct from Portland. Costa Rica eco-adventure travel (Manuel Antonio, Arenal, Monteverde) is common with the central coast's outdoor and environmental community. These winter travel bookings happen in September–October — and passport expiration is often discovered at the same time.",
        tip: "September and October are when Lincoln City residents book Mexico and Costa Rica winter trips. Check passport expiration immediately at booking. If it expires before May of next year, renew now through Fast Passport Center — expedited from Portland FedEx in 2–9 business days.",
      },
    ],
    callout: {
      icon: "ri-water-flash-line",
      title: "Lincoln City, Oregon — Central Coast Gateway",
      description: "Lincoln City is the commercial hub of the central Oregon coast — the intersection of Pacific beach lifestyle, Siletz tribal heritage, Devils Lake recreation, and Willamette Valley weekend escapes. Its position on US-18 gives Lincoln City better Portland access than most coastal communities. Fast Passport Center has served Lincoln City and Lincoln County residents with expedited passports for Mexico cruise trips, BC adventures, and international family travel.",
    },
  },
  destinations: {
    title: "Where Lincoln City Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Lincoln City and central Oregon coast clients. Mexico cruises, BC outdoor adventure, and winter tropical escapes create distinctive passport urgency situations for Lincoln County residents.",
    stats: [
      { stat: "90 mi", label: "to Portland International Airport (PDX) via US-18 — primary international hub" },
      { stat: "320 mi", label: "to Seattle Passport Agency — the nearest regional passport office" },
      { stat: "5.5 hrs", label: "to Vancouver BC — Lincoln City's most accessible international destination" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Lincoln City" },
    ],
    destinations: [
      { name: "Mexico — Puerto Vallarta, Cabo, Mazatlan", context: "Mexico cruises from LA and winter direct flights from Portland — the #1 international destination for Lincoln City area residents", urgency: "October Mexico cruise bookings; passport found expired while packing for January departure", icon: "ri-ship-2-line" },
      { name: "British Columbia — Vancouver, Whistler", context: "Central coast's closest international destination — 5 hours north to BC via Portland and I-5 to Blaine", urgency: "Whistler ski season booked November; BC summer trips in March — passport discovered expired at booking time", icon: "ri-map-pin-2-line" },
      { name: "Costa Rica & Central America", context: "Eco-adventure and beach travel for Lincoln City's outdoor and environmental community — dry season November through April", urgency: "October Costa Rica bookings; passport expired discovered when gathering documents in December", icon: "ri-leaf-line" },
      { name: "Alaska — Inside Passage Cruises", context: "Seattle cruise departures for Alaska's Inside Passage — popular with central Oregon coast residents for marine wildlife and glacier viewing", urgency: "Alaska cruise bookings in September; passport expired discovered at final payment stage in March", icon: "ri-ship-2-line" },
      { name: "Europe — Scotland, Germany, Portugal", context: "Heritage travel and cultural tourism for Lincoln City-area families with European roots", urgency: "Summer Europe trips booked in January–February; passport renewals needed by March for June departure", icon: "ri-global-line" },
      { name: "Japan & Pacific Rim", context: "Cultural travel and Pacific Rim outdoors for central coast residents — Japan whale watching, Pacific island extensions, and teaching abroad programs", urgency: "Teaching abroad programs require passports months before departure — apply immediately at acceptance", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the central and north Oregon coast with expedited passport expertise.",
    crossLinks: [
      { name: "Newport", route: "/cities/newport" },
      { name: "Portland", route: "/cities/portland" },
      { name: "Salem", route: "/cities/salem" },
    ],
  },
};